import differenceInDays from 'date-fns/differenceInDays'
import { useCallback, useEffect, useState } from 'react'

import { AddAccountPaths } from '../../config/routes'
import { useAccounts } from '../../hooks/use-accounts'
import { useTransactions } from '../../hooks/use-transactions'
import { Notify } from '../../lib/helpers'
import { MasaConnectionProvider } from '../../models'

const BANK_TRANSACTION_SCORE = 0.1
const CRYPTO_TRANSACTION_SCORE = 0.05

const calculateInit = () => {
    const agreement = localStorage.getItem('data-farming-agreement')
    const cacheDateIso = localStorage.getItem('rewards_calc_datetime')

    const cacheDate = new Date(cacheDateIso ?? '')

    const difference = differenceInDays(new Date(), cacheDate)
    if (agreement && !cacheDateIso) return true
    return agreement && difference > 0
}

export function RewardsFetcher() {
    const [done, setDone] = useState(false)
    const [agreed] = useState(calculateInit())

    const {
        transactions: bankTransactions,
        updateNextToken: bankUpdateToken,
        loading: bankLoading,
        nextAvailable: nextAvailableBank,
    } = useTransactions({
        type: agreed ? 'bank' : undefined,
        endDate: undefined,
        startDate: undefined,
    })
    const {
        transactions: cryptoTransactions,
        updateNextToken: cryptoUpdateToken,
        loading: cryptoLoading,
        nextAvailable: nextAvailableCrypto,
    } = useTransactions({
        type: agreed ? 'crypto' : undefined,
        endDate: undefined,
        startDate: undefined,
    })

    const { accounts: cryptoAccounts, loading: accountsLoadingCrypto } =
        useAccounts({
            type: MasaConnectionProvider.CRYPTO,
        })
    const { accounts: bankAccounts, loading: accountsLoadingBank } =
        useAccounts({
            type: MasaConnectionProvider.PLAID,
        })

    useEffect(() => {
        if (nextAvailableCrypto) cryptoUpdateToken()
        if (nextAvailableBank) bankUpdateToken()

        if (
            !nextAvailableCrypto &&
            !nextAvailableBank &&
            !bankLoading &&
            !cryptoLoading &&
            !accountsLoadingCrypto &&
            !accountsLoadingBank
        ) {
            setDone(true)
        }
    }, [
        nextAvailableCrypto,
        nextAvailableBank,
        bankLoading,
        cryptoLoading,
        accountsLoadingCrypto,
        accountsLoadingBank,
        bankUpdateToken,
        cryptoUpdateToken,
    ])

    const calcRewards = useCallback(() => {
        const bankPoints = bankTransactions.length * BANK_TRANSACTION_SCORE
        const cryptoPoints =
            cryptoTransactions.length * CRYPTO_TRANSACTION_SCORE

        return bankPoints + cryptoPoints
    }, [cryptoTransactions, bankTransactions])

    useEffect(() => {
        if (agreed) {
            if (!done)
                Notify(
                    'warning',
                    'Calculating rewards',
                    'We are fetching your transactions in order to calculate your rewards',
                )
            else {
                if (cryptoAccounts.length === 0 && bankAccounts.length === 0) {
                    Notify(
                        'warning',
                        'Whoops',
                        (
                            <>
                                We are tried to calculate your rewards but you
                                dont have accounts,{' '}
                                <a href={AddAccountPaths.ADD_ACCOUNT}>
                                    go and link your accounts
                                </a>
                            </>
                        ) as any,
                    )
                } else {
                    localStorage.setItem(
                        'bank_transactions',
                        bankTransactions.length,
                    )
                    localStorage.setItem(
                        'crypto_transactions',
                        cryptoTransactions.length,
                    )
                    localStorage.setItem(
                        'bank_reward_points',
                        (
                            bankTransactions.length * BANK_TRANSACTION_SCORE
                        ).toString(),
                    )
                    localStorage.setItem(
                        'crypto_reward_points',
                        (
                            cryptoTransactions.length * CRYPTO_TRANSACTION_SCORE
                        ).toString(),
                    )

                    localStorage.setItem(
                        'crypto_accounts',
                        cryptoAccounts.length,
                    )
                    localStorage.setItem('bank_accounts', bankAccounts.length)

                    localStorage.setItem(
                        'rewards_calc_datetime',
                        new Date().toISOString().toString(),
                    )
                    Notify(
                        'success',
                        'Rewards calculated!',
                        `Your current reward points are: ${calcRewards()} `,
                    )
                }
            }
        }
        // eslint-disable-next-line
    }, [done])

    return <></>
}
